/**
 * Список товаров исполнителя.
 *
 * Снятый с продажи товар остаётся в списке с пометкой, а не исчезает: он
 * никуда не делся из базы (на него ссылаются прошлые заказы), и человек
 * должен понимать, что «удалил» — значит «спрятал от клиентов», и вернуть
 * его можно тем же переключателем.
 */
import type { CurrentUser, VendorProduct } from '@arbuz/shared';
import { formatMoney, makeT } from '../i18n';
import { haptic } from '../tg';
import { secondaryButton } from './courierUi';
import { EmptyState, hintStyle } from './vendorUi';

interface Props {
  user: CurrentUser;
  products: VendorProduct[] | null;
  error: string | null;
  onOpen: (id: number | 'new') => void;
  onRetry: () => void;
  onBack: () => void;
}

export function VendorProducts({ user, products, error, onOpen, onRetry, onBack }: Props) {
  const t = makeT(user.locale);

  return (
    <main className="page">
      <h1 className="h1">{t('shopSectionProducts')}</h1>

      {error && (
        <EmptyState
          tone="error"
          title={t('productsLoadError')}
          text={error}
          actionLabel={t('retry')}
          onAction={onRetry}
        />
      )}

      {!error && products === null && <p className="muted">{t('loading')}</p>}

      {!error && products !== null && products.length === 0 && (
        <EmptyState
          title={t('productsEmpty')}
          text={t('productsEmptyHint')}
          actionLabel={t('productAdd')}
          onAction={() => onOpen('new')}
        />
      )}

      {!error && products !== null && products.length > 0 && (
        <ul style={{ listStyle: 'none', margin: 'var(--s-4) 0 0', padding: 0, display: 'grid', gap: 'var(--s-2)' }}>
          {products.map((p) => (
            <li key={p.id}>
              <button
                type="button"
                onClick={() => {
                  haptic('light');
                  onOpen(p.id);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--s-3)',
                  width: '100%',
                  minHeight: 'var(--tap-min)',
                  padding: 'var(--s-2)',
                  borderRadius: 'var(--r-lg)',
                  border: '1px solid var(--c-border)',
                  background: 'var(--c-surface)',
                  color: 'var(--c-text)',
                  font: 'inherit',
                  textAlign: 'left',
                  cursor: 'pointer',
                  // Снятый товар приглушён, но подпись ниже говорит о том же
                  opacity: p.is_available ? 1 : 0.6,
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    flex: '0 0 auto',
                    width: 48,
                    height: 48,
                    borderRadius: 'var(--r-md)',
                    background: p.image_url ? `center/cover url(${p.image_url})` : 'var(--c-surface-alt)',
                  }}
                />
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span
                    style={{
                      display: 'block',
                      font: 'var(--fw-medium) var(--fs-body)/var(--lh-body) var(--font)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {p.name}
                  </span>
                  <span
                    style={{
                      display: 'block',
                      marginTop: 'var(--s-1)',
                      fontSize: 'var(--fs-sm)',
                      color: 'var(--c-text-secondary)',
                    }}
                  >
                    {formatMoney(p.price, user.locale)}
                    {!p.is_available && ` · ${t('productHidden')}`}
                  </span>
                </span>
                <span aria-hidden="true" style={{ color: 'var(--c-text-tertiary)' }}>
                  ›
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      <p style={{ ...hintStyle, marginTop: 'var(--s-4)' }}>{t('productsEmptyHint')}</p>

      <div style={{ display: 'grid', gap: 'var(--s-2)', marginTop: 'var(--s-4)' }}>
        <button type="button" className="btn-primary" onClick={() => onOpen('new')}>
          {t('productAdd')}
        </button>
        <button type="button" style={secondaryButton} onClick={onBack}>
          {t('shopTitle')}
        </button>
      </div>
    </main>
  );
}
